import { useState } from 'react';
import { Loader2, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import { useAuth } from '@/App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WingmanInsight({ analysisType, analysisData }) {
  const { token, user } = useAuth();
  const isTrial = user?.is_demo || !user?.is_unlimited;
  const [insight, setInsight] = useState('');
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [fetched, setFetched] = useState(false);

  const getInsight = async () => {
    if (fetched) { setExpanded(!expanded); return; }
    setExpanded(true);
    setLoading(true);
    try {
      // SFR Calculator is a special-case path. The other modules use feed-data
      // checks to drive the FCR commentary; SFR has NO actual feed-given (just
      // a recommendation from the installed table). Treating recommendation as
      // "feed given" produced misleading "Feed volumes are recorded" lines, so
      // we route SFR through its own rules block calibrated by the user's
      // PRD ask.
      const isSFR = analysisType === 'SFR Calculator';

      // Pre-determine data availability so the AI doesn't guess wrong
      const hasFeedData = !!(analysisData?.total_feed_given || analysisData?.total_feed_kg || analysisData?.feeding_days > 0 || analysisData?.reported_fcr || analysisData?.biologicalFCR || analysisData?.fcrResult);
      const hasFCR = !!(
        analysisData?.reported_fcr || 
        analysisData?.fcrResult?.reported?.fcr || 
        analysisData?.biologicalFCR?.reportedFCR ||
        analysisData?.closing_biological_fcr ||
        analysisData?.reported_biological_fcr ||
        (analysisData?.fcrResult?.reported && analysisData?.fcrResult?.adjusted)
      );
      const hasReportedBioFCR = !!(analysisData?.reported_fcr || analysisData?.reported_biological_fcr);
      
      const fcrInstruction = hasFeedData
        ? `Feed data IS available for this cage. ${hasFCR 
            ? 'Biological FCR has been calculated from feed data. Compare reported FCR vs model FCR and state whether the gap is significant. Reference the adjusted FCR if missing fish are indicated.' 
            : 'Feed volumes are recorded. Summarise the feeding performance and note any overfeeding or underfeeding signals.'}`
        : hasReportedBioFCR
          ? 'The user provided a reported biological FCR value. Use it for comparison against the model FCR, but note this is a user-reported figure — not derived from daily feed data in this analysis.'
          : 'Feed data was NOT provided for this analysis. State: "FCR confirmation is not available for this analysis because feed data was not provided."';

      // Pre-determine detection confidence to prevent contradictions
      const detStrength = analysisData?.detection_strength || analysisData?.ssot?.detectionStrength || analysisData?.detectionResults?.detectionStrength || '';
      const actionability = analysisData?.actionability || analysisData?.ssot?.actionability || analysisData?.decision_class || null;
      const isLowConfidence = !detStrength || detStrength === 'Low' || detStrength === 'Indication Only' || actionability === 'MONITOR_ONLY';
      const confidenceInstruction = isLowConfidence
        ? 'CRITICAL: Detection confidence is LOW (Indication Only / MONITOR_ONLY). You MUST NOT say "Missing fish signal detected" or present any missing fish estimate as a finding. Use the exact phrase "Indicative deviation observed — insufficient data for confirmation". When describing the pattern, say "A deviation pattern is observed that may indicate stock discrepancy" — do NOT use definitive wording like "fewer fish are likely consuming feed" or "missing fish confirmed". Recommend collecting more samples. Do NOT cite a specific missing fish number.'
        : `Detection confidence is ${detStrength}. You may reference the estimated missing fish count but qualify it with the confidence level. Prefer "Observed pattern is consistent with fewer fish than assumed" over definitive claims.`;

      // Pre-determine feeding classification so the AI mirrors the SSOT reconciled state
      const fs = analysisData?.feeding_state;
      const fn = analysisData?.feeding_note;
      const fRfi = analysisData?.feeding_rfi_pct;
      const wSig = analysisData?.waste_economically_significant;
      const feedingInstruction = fs
        ? `Feeding classification is "${fs}" (RFI ${fRfi != null ? fRfi + '%' : 'n/a'}). ${fn || ''} ${
            fs === 'Mixed' && !wSig
              ? 'This is a Mixed pattern with MINOR period-level variance — do NOT describe it as an "overfeeding risk". Use the phrase "Mixed feeding pattern" and note that period waste is not economically significant.'
              : fs === 'Mixed' && wSig
                ? 'This is a Mixed pattern with ECONOMICALLY SIGNIFICANT period-level overfeeding — describe it as "Mixed feeding pattern" and flag the period waste explicitly with the €/kg impact only.'
                : fs === 'Overfeeding'
                  ? 'Overfeeding is DOMINANT (cumulative RFI > 100%) — describe it as overfeeding and recommend reducing feed allocation.'
                  : fs === 'Restricted'
                    ? 'Feeding is RESTRICTED (RFI < 85%) — describe as underfeeding/restriction. Do NOT cite feed waste or overfeeding risk.'
                    : 'Feeding is OPTIMAL — do NOT flag overfeeding.'
          }`
        : 'No feeding classification is available — do not speculate about overfeeding or underfeeding.';

      // Pre-determine if any deviation evidence exists (Growth-Fit estimate,
      // jump signal, fish-count deviation, indication-only) so the AI cannot
      // emit "No stock-deviation signal indicated" while a discrepancy is
      // visible on the user's screen — this is the Growth Prognosis
      // contradiction reported by users.
      const hasDeviationEvidence = !!(
        analysisData?.missing_fish_detected ||
        analysisData?.has_missing_fish ||
        (typeof analysisData?.missing_fish_count === 'number' && analysisData.missing_fish_count > 0) ||
        analysisData?.growth_fit_deviation ||
        (analysisData?.growth_fit && (analysisData.growth_fit.detected || analysisData.growth_fit.estimatedMissing > 0)) ||
        analysisData?.ssot?.hasMissingFish ||
        (typeof analysisData?.ssot?.missingFishCount === 'number' && analysisData.ssot.missingFishCount > 0) ||
        analysisData?.ssot?.growthFitHasDeviation ||
        actionability === 'MONITOR_ONLY'
      );
      const deviationContradictionGuard = hasDeviationEvidence
        ? '- DEVIATION-CONTRADICTION GUARD (HARD): a deviation estimate is visible on this report (Growth-Fit / fish-count / indication-only / monitor-only). You MUST NEVER write "No stock-deviation signal indicated", "Detection: Normal", or any paraphrase that denies the deviation. Use ONE of these calibrated phrases instead: "No confirmed stock-deviation signal" OR "Low-confidence deviation signal remains under review". Treat the deviation as indicative-only unless confidence is high.'
        : '- When no deviation evidence exists you may write "No confirmed stock-deviation signal" — never "Normal" if any monitor-only or low-reliability flag is active.';

      const rules = [
        'You are a professional aquaculture analyst. Provide a concise, decision-focused summary for a production manager.',
        '',
        'OPERATIONAL FOCUS (PRD final-pass UX simplification):',
        '- The Detection panel, Operational Decision State, and the headline KPIs already restate the signal classification on screen. Your job is to add NEW operational meaning and practical action — DO NOT repeat the same classification ("below model", "monitor only", "validation required", "indicative deviation", "deviation observed", "insufficient confidence") in multiple sections of your answer. State each conclusion at most ONCE, and use the rest of the response to answer "so what" — what does this mean for the next decision and what is the operator expected to do.',
        '- A production manager should grasp the operational state of the cage within ~15 seconds of reading your response. Lead with action.',
        '',
        'STRICT OUTPUT FORMAT:',
        '**Summary** — One sentence headline of the overall cage status.',
        '**What looks good** — Positive performance indicators (growth alignment, feeding efficiency, mortality control).',
        '**What needs attention** — Any concerns about stock deviation, growth gaps, or data quality.',
        `**FCR status** — ${fcrInstruction}`,
        '**Recommended action** — One clear, practical next step.',
        '',
        'CONTRADICTION PREVENTION RULES (CRITICAL):',
        '- NEVER say growth is "tracking as expected" AND also say there is a missing fish problem in the same response. Pick one direction.',
        '- If the adjusted model is significantly below the standard model (e.g., adjusted to 71%), this IS the concern. Do not praise growth for matching a lowered bar — instead note that actual performance is below the species standard.',
        '- If confidence is low or data is sparse, do NOT cite a specific missing fish number. Only say more data is needed.',
        '- NEVER say "no confirmed signal" and then give a specific fish count in the same response. These are contradictory.',
        '- LOW-FCR / MISSING-FISH RULE (HARD): when a missing-fish signal is active (estimated_fish_count < expected_fish_count, or any non-zero fish_difference < 0, or "Possible stock deviation" classification), you MUST NOT cite an FCR ratio < 1.0 (or below the species range) under "What looks good". Treat low FCR in this context as a RED FLAG of under-reported mortality / phantom efficiency, and surface it under "What needs attention" with a one-line caveat: "Apparent FCR efficiency is likely an artefact of unrecorded losses — confirm stock count before drawing conclusions." Never use the words "efficient", "good feed conversion", or "tracking well" about the FCR in that case.',
        '- "What looks good" must remain SHORT (≤ 1 line) when any negative signal is present, and may be omitted entirely if there is genuinely nothing positive to highlight.',
        `- ${confidenceInstruction}`,
        deviationContradictionGuard,
        `- FEEDING: ${feedingInstruction}`,
        '- NEVER flag "overfeeding risk" when the reconciled feeding classification is Mixed-without-significance, Optimal, or Restricted. Mirror the SSOT classification exactly.',
        '',
        'OTHER RULES:',
        '- If best_fit_factor exists and is not 1, explain what the adjustment means in plain terms (e.g., "actual growth is running at 71% of the standard model").',
        '- Do not attribute lower FCR to biological outperformance when missing fish are present.',
        '- Never mention variable names, null values, system logic, or code references.',
        '- Keep language professional, concise, and operational.',
        '- For Snapshot Analysis with a single data point, do not speculate on causes.',
        '- Use aquaculture journal benchmarks when relevant (e.g., Sea Bream FCR typically 1.2–1.8, optimal growth 20–25°C).',
        '- Maximum 5 bullet points total.',
      ].join('\n');

      // SFR Calculator-only rules block. Replaces the generic FCR/missing-fish
      // commentary path with copy that reflects what the tool actually does
      // — a daily-feed recommendation from the installed SFR table. PRD ask:
      // (1) drop the misleading "Feed volumes are recorded" line, (2) clarify
      // what the tool computes, (3) point users at custom SFR tables for
      // site-specific accuracy.
      const sfrRules = [
        'You are a professional aquaculture analyst. Provide a concise, decision-focused summary of an SFR Calculator result for a production manager.',
        '',
        'WHAT THE TOOL DOES:',
        'The SFR Calculator recommends how much feed should be given per day based on species, temperature, fish count, and average weight, using the installed SFR table.',
        '',
        'STRICT OUTPUT FORMAT:',
        '**Summary** — One sentence on the recommended daily feed for these conditions.',
        '**What this number means** — Restate the recommendation in plain language (e.g. "≈X kg/day for this cage at this temperature and weight").',
        '**FCR status** — You MUST write exactly: "Recommended feed volume is generated from the installed SFR table, but FCR cannot be assessed from the SFR recommendation alone."',
        '**Recommended action** — Use this value as the daily feed recommendation generated from the installed SFR table, then adjust operationally based on appetite, behaviour, oxygen, health status, and farm conditions.',
        '**Custom SFR table guidance** — Include exactly: "For highest accuracy, update or upload your own site-specific SFR table so recommendations match your farm conditions and production objectives."',
        '',
        'STRICT WORDING RULES (CRITICAL):',
        '- NEVER write "Feed volumes are recorded" or any paraphrase that implies actual feed has been given/recorded — this tool produces a recommendation only.',
        '- NEVER assess FCR, growth performance, or stock discrepancy from this output. Those require multi-day data.',
        '- NEVER say "missing fish detected", "stock deviation detected", or any claim that requires longitudinal data.',
        '- NEVER reference "marine fish", "marine finfish", or "Mediterranean finfish" when the species is Tilapia.',
        '- Keep total length to 5 short bullets max. Professional, operational tone.',
      ].join('\n');

      const sfrPrompt = `${sfrRules}\n\nSFR Calculator result:\n\n${JSON.stringify(analysisData, null, 2)}`;

      const prompt = `${rules}\n\nAnalyse this ${analysisType} result:\n\n${JSON.stringify(analysisData, null, 2)}`;
      const res = await axios.post(`${API}/wingman/chat`, {
        message: isSFR ? sfrPrompt : prompt,
        session_id: `insight-${analysisType}-${Date.now()}`
      }, { headers: { Authorization: `Bearer ${token}` } });
      setInsight(res.data.response);
      setFetched(true);
    } catch {
      setInsight('Could not generate insight. Try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-6 rounded-xl overflow-hidden shadow-lg border border-teal-300/60" data-testid="wingman-insight">
      <button
        onClick={getInsight}
        className="w-full px-5 py-4 flex items-center justify-between text-left bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 transition-all duration-200 group"
        data-testid="wingman-insight-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center h-8 w-8 rounded-full bg-white/20 backdrop-blur-sm">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-white">
              <path d="M12 2C6.48 2 2 5.58 2 10c0 2.24 1.12 4.26 2.92 5.7L3 22l4.35-2.18C8.78 20.27 10.35 20.5 12 20.5c5.52 0 10-3.08 10-7.25S17.52 2 12 2z" />
            </svg>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-base font-bold text-white tracking-wide">Ask Wingman for AI Insight</span>
              {isTrial && (
                <span className="inline-flex items-center gap-1 bg-amber-400/90 text-amber-950 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" data-testid="wingman-insight-premium-badge">
                  <Sparkles className="h-3 w-3" />
                  Premium
                </span>
              )}
            </div>
            <span className="block text-xs text-white/70 font-normal mt-0.5">
              {isTrial ? 'You have access to this premium feature — try it now' : 'AI-powered analysis backed by aquaculture science — FCR benchmarks, species data, and production best practices'}
            </span>
          </div>
          {loading && <Loader2 className="h-5 w-5 animate-spin text-white" />}
        </div>
        {expanded ? <ChevronUp className="h-5 w-5 text-white/80 group-hover:text-white" /> : <ChevronDown className="h-5 w-5 text-white/80 group-hover:text-white" />}
      </button>
      {expanded && (
        <div className="px-5 pb-4 bg-white border-t border-teal-100">
          {loading ? (
            <div className="flex items-center gap-2 py-4 text-sm text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin text-teal-500" /> Analysing your results...
            </div>
          ) : (
            <div className="pt-3 prose prose-sm prose-slate max-w-none [&_p]:text-sm [&_p]:my-1 [&_ul]:my-1 [&_ul]:pl-4 [&_li]:text-sm [&_li]:my-0.5 [&_strong]:text-slate-800">
              <ReactMarkdown>{insight}</ReactMarkdown>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
